import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "talk" }
const _hoisted_2 = { class: "talk__inner" }
const _hoisted_3 = { class: "talk__composer-actions" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "talk__error",
  role: "alert"
}
const _hoisted_6 = {
  key: 4,
  class: "talk__feed",
  "aria-label": "نظرات"
}
const _hoisted_7 = { class: "talk__card-head" }
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "talk__meta" }
const _hoisted_10 = { class: "talk__body" }
const _hoisted_11 = { class: "talk__actions" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["fill"]
const _hoisted_14 = ["aria-expanded", "onClick"]
const _hoisted_15 = { class: "talk__replies" }
const _hoisted_16 = { class: "talk__reply-head" }
const _hoisted_17 = ["src"]
const _hoisted_18 = { class: "talk__meta" }
const _hoisted_19 = { class: "talk__body talk__body--sm" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = ["fill"]
const _hoisted_22 = ["onSubmit"]
const _hoisted_23 = ["onUpdate:modelValue"]
const _hoisted_24 = ["disabled"]

import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import {
  createCommunityPost,
  createCommunityReply,
  listCommunityPosts,
  togglePostLike,
  toggleReplyLike,
} from "@/api/community";
import { handleError } from "@/services/errorHandler";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import ErrorMessage from "@/components/common/ErrorMessage.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import type { CommunityAuthor, CommunityPost, CommunityReply } from "@/types";
import { resolveAvatarSrc } from "@/constants/avatars";


export default /*@__PURE__*/_defineComponent({
  __name: 'CommunityTalk',
  setup(__props) {

const AVATAR_COLORS = [
  { bg: "#f4a97a", color: "#ffffff" },
  { bg: "#6ee7b7", color: "#ffffff" },
  { bg: "#86efac", color: "#ffffff" },
  { bg: "#fca5a5", color: "#ffffff" },
  { bg: "#93c5fd", color: "#ffffff" },
  { bg: "#c4b5fd", color: "#ffffff" },
];

const router = useRouter();
const posts = ref<CommunityPost[]>([]);
const loading = ref(true);
const posting = ref(false);
const replyingId = ref<number | null>(null);
const errorMessage = ref<string | null>(null);
const formError = ref<string | null>(null);
const newPost = ref("");
const expanded = reactive<Record<number, boolean>>({});
const replyDrafts = reactive<Record<number, string>>({});

function toPersianDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);
}

function displayName(author: CommunityAuthor): string {
  return author.username?.trim() || `کاربر #${author.id}`;
}

function initials(author: CommunityAuthor): string {
  const name = author.username?.trim();
  if (!name) return "؟";
  return name
    .split(/[\s_]+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function avatarStyle(userId: number) {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

function authorAvatar(author: CommunityAuthor): string | null {
  return resolveAvatarSrc(author.avatar_url);
}

function formatTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fa-IR");
}

async function load() {
  loading.value = true;
  errorMessage.value = null;
  try {
    const data = await listCommunityPosts({ page: 1, size: 50 });
    posts.value = data.items || [];
  } catch (e) {
    errorMessage.value = handleError(e, "CommunityTalk.load");
  } finally {
    loading.value = false;
  }
}

async function submitPost() {
  if (posting.value || !newPost.value.trim()) return;
  posting.value = true;
  formError.value = null;
  try {
    const created = await createCommunityPost(newPost.value.trim());
    posts.value = [created, ...posts.value];
    newPost.value = "";
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.submitPost");
  } finally {
    posting.value = false;
  }
}

function toggleReplies(postId: number) {
  expanded[postId] = !expanded[postId];
}

async function submitReply(post: CommunityPost) {
  const body = (replyDrafts[post.id] || "").trim();
  if (!body || replyingId.value === post.id) return;
  replyingId.value = post.id;
  formError.value = null;
  try {
    const reply = await createCommunityReply(post.id, body);
    post.replies = [...(post.replies || []), reply];
    post.reply_count = post.replies.length;
    replyDrafts[post.id] = "";
    expanded[post.id] = true;
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.submitReply");
  } finally {
    replyingId.value = null;
  }
}

async function likePost(post: CommunityPost) {
  try {
    const result = await togglePostLike(post.id);
    post.liked_by_me = result.liked;
    post.like_count = result.like_count;
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.likePost");
  }
}

async function likeReply(post: CommunityPost, reply: CommunityReply) {
  try {
    const result = await toggleReplyLike(reply.id);
    reply.liked_by_me = result.liked;
    reply.like_count = result.like_count;
    const idx = post.replies.findIndex((r) => r.id === reply.id);
    if (idx >= 0) post.replies[idx] = { ...reply };
  } catch (e) {
    formError.value = handleError(e, "CommunityTalk.likeReply");
  }
}

function goBack() {
  if (window.history.state && window.history.state.back) {
    router.back();
  } else {
    router.push("/dashboard");
  }
}

onMounted(load);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        type: "button",
        class: "talk__back",
        onClick: goBack
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          width: "20",
          height: "20",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "aria-hidden": "true"
        }, [
          _createElementVNode("path", {
            d: "M5 12H19",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          }),
          _createElementVNode("path", {
            d: "M13 6l6 6-6 6",
            "stroke-linecap": "round",
            "stroke-linejoin": "round"
          })
        ], -1),
        _createElementVNode("span", null, "بازگشت", -1)
      ]))]),
      _cache[5] || (_cache[5] = _createElementVNode("header", { class: "talk__header" }, [
        _createElementVNode("p", { class: "talk__eyebrow" }, "جامعه یادگیری"),
        _createElementVNode("h1", { class: "talk__title" }, "صحبت کنیم"),
        _createElementVNode("p", { class: "talk__subtitle" }, " سؤال بپرس، تجربه رد و بدل کن، و به بقیه انگیزه بده. ")
      ], -1)),
      _createElementVNode("form", {
        class: "talk__composer",
        onSubmit: _withModifiers(submitPost, ["prevent"])
      }, [
        _withDirectives(_createElementVNode("textarea", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newPost).value = $event)),
          rows: "3",
          maxlength: "4000",
          placeholder: "چی تو ذهنته؟ اینجا بنویس...",
          required: ""
        }, null, 512), [
          [_vModelText, newPost.value]
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            type: "submit",
            class: "talk__btn talk__btn--primary",
            disabled: posting.value || !newPost.value.trim()
          }, _toDisplayString(posting.value ? "در حال ارسال..." : "ارسال نظر"), 9, _hoisted_4)
        ])
      ], 32),
      (formError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(formError.value), 1))
        : _createCommentVNode("", true),
      (loading.value)
        ? (_openBlock(), _createBlock(LoadingSpinner, {
            key: 1,
            message: "در حال بارگذاری گفتگوها..."
          }))
        : (errorMessage.value)
          ? (_openBlock(), _createBlock(ErrorMessage, {
              key: 2,
              message: errorMessage.value,
              onRetry: load
            }, null, 8, ["message"]))
          : (!posts.value.length)
            ? (_openBlock(), _createBlock(EmptyState, {
                key: 3,
                title: "هنوز حرفی زده نشده",
                message: "اولین نفر باش و گفتگو رو شروع کن."
              }))
            : (_openBlock(), _createElementBlock("section", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(posts.value, (post) => {
                  return (_openBlock(), _createElementBlock("article", {
                    key: post.id,
                    class: "talk__card"
                  }, [
                    _createElementVNode("header", _hoisted_7, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["talk__avatar", { 'talk__avatar--image': !!authorAvatar(post.author) }]),
                        style: _normalizeStyle(authorAvatar(post.author) ? undefined : avatarStyle(post.author.id))
                      }, [
                        (authorAvatar(post.author))
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: authorAvatar(post.author)!,
                              alt: "",
                              class: "talk__avatar-img"
                            }, null, 8, _hoisted_8))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode(_toDisplayString(initials(post.author)), 1)
                            ], 64))
                      ], 6),
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("strong", null, _toDisplayString(displayName(post.author)), 1),
                        _createElementVNode("time", null, _toDisplayString(formatTime(post.created_at)), 1)
                      ])
                    ]),
                    _createElementVNode("p", _hoisted_10, _toDisplayString(post.body), 1),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["talk__action", { 'talk__action--active': post.liked_by_me }]),
                        onClick: ($event: any) => (likePost(post))
                      }, [
                        (_openBlock(), _createElementBlock("svg", {
                          viewBox: "0 0 24 24",
                          width: "16",
                          height: "16",
                          fill: post.liked_by_me ? 'currentColor' : 'none',
                          stroke: "currentColor",
                          "stroke-width": "2",
                          "aria-hidden": "true"
                        }, [...(_cache[2] || (_cache[2] = [
                          _createElementVNode("path", {
                            d: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, null, -1)
                        ]))], 8, _hoisted_13)),
                        _createTextVNode(" " + _toDisplayString(toPersianDigits(post.like_count)), 1)
                      ], 10, _hoisted_12),
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["talk__action", { 'talk__action--open': expanded[post.id] }]),
                        "aria-expanded": !!expanded[post.id],
                        onClick: ($event: any) => (toggleReplies(post.id))
                      }, [
                        _cache[3] || (_cache[3] = _createElementVNode("svg", {
                          viewBox: "0 0 24 24",
                          width: "16",
                          height: "16",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2",
                          "aria-hidden": "true"
                        }, [
                          _createElementVNode("path", {
                            d: "M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          })
                        ], -1)),
                        (expanded[post.id])
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createTextVNode("مخفی کردن پاسخ‌ها")
                            ], 64))
                          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                              _createTextVNode(_toDisplayString(post.reply_count > 0
                    ? `نمایش پاسخ‌ها (${toPersianDigits(post.reply_count)})`
                    : "پاسخ بده"), 1)
                            ], 64))
                      ], 10, _hoisted_14)
                    ]),
                    _withDirectives(_createElementVNode("div", _hoisted_15, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(post.replies, (reply) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: reply.id,
                          class: "talk__reply"
                        }, [
                          _createElementVNode("div", _hoisted_16, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["talk__avatar talk__avatar--sm", { 'talk__avatar--image': !!authorAvatar(reply.author) }]),
                              style: _normalizeStyle(authorAvatar(reply.author) ? undefined : avatarStyle(reply.author.id))
                            }, [
                              (authorAvatar(reply.author))
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: authorAvatar(reply.author)!,
                                    alt: "",
                                    class: "talk__avatar-img"
                                  }, null, 8, _hoisted_17))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createTextVNode(_toDisplayString(initials(reply.author)), 1)
                                  ], 64))
                            ], 6),
                            _createElementVNode("div", _hoisted_18, [
                              _createElementVNode("strong", null, _toDisplayString(displayName(reply.author)), 1),
                              _createElementVNode("time", null, _toDisplayString(formatTime(reply.created_at)), 1)
                            ])
                          ]),
                          _createElementVNode("p", _hoisted_19, _toDisplayString(reply.body), 1),
                          _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["talk__action talk__action--sm", { 'talk__action--active': reply.liked_by_me }]),
                            onClick: ($event: any) => (likeReply(post, reply))
                          }, [
                            (_openBlock(), _createElementBlock("svg", {
                              viewBox: "0 0 24 24",
                              width: "14",
                              height: "14",
                              fill: reply.liked_by_me ? 'currentColor' : 'none',
                              stroke: "currentColor",
                              "stroke-width": "2",
                              "aria-hidden": "true"
                            }, [...(_cache[4] || (_cache[4] = [
                              _createElementVNode("path", {
                                d: "M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z",
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round"
                              }, null, -1)
                            ]))], 8, _hoisted_21)),
                            _createTextVNode(" " + _toDisplayString(toPersianDigits(reply.like_count)), 1)
                          ], 10, _hoisted_20)
                        ]))
                      }), 128)),
                      _createElementVNode("form", {
                        class: "talk__reply-form",
                        onSubmit: _withModifiers(($event: any) => (submitReply(post)), ["prevent"])
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((replyDrafts[post.id]) = $event),
                          type: "text",
                          maxlength: "2000",
                          placeholder: "پاسخت رو بنویس...",
                          required: ""
                        }, null, 8, _hoisted_23), [
                          [_vModelText, replyDrafts[post.id]]
                        ]),
                        _createElementVNode("button", {
                          type: "submit",
                          class: "talk__btn talk__btn--primary talk__btn--sm",
                          disabled: replyingId.value === post.id || !(replyDrafts[post.id] || '').trim()
                        }, " ارسال ", 8, _hoisted_24)
                      ], 40, _hoisted_22)
                    ], 512), [
                      [_vShow, expanded[post.id]]
                    ])
                  ]))
                }), 128))
              ]))
    ])
  ]))
}
}

})